/**
 * Auth Provider for React-Admin v5
 *
 * WHY THIS EXISTS (vs relying solely on httpClient interceptors):
 * React-Admin calls checkAuth() on every navigation and checkError() on every
 * API error. These are the framework's hooks for deciding "is the user still
 * logged in?" If we only had interceptors, React-Admin would never know a
 * session expired — it would just see failed requests. The authProvider bridges
 * React-Admin's auth lifecycle with our tokenRefreshService.
 *
 * REFRESH COORDINATION:
 * Both this file and httpClient.ts call tokenRefreshService.refreshToken().
 * That's safe because tokenRefreshService uses a singleton gate — only the
 * first caller actually hits Cognito; all others await the same result.
 * There is NO duplicate refresh logic here; this file only calls into
 * tokenRefreshService, never manages tokens directly.
 *
 * ENVIRONMENT ISOLATION:
 * localStorage is origin-scoped. Dev (localhost:3000) and Staging
 * (admin.deeliver-staging.co.za) have completely separate storage.
 * No cross-environment token leakage is possible.
 */

import type { AuthProvider } from 'react-admin';
import { adminSignInRefusal } from './adminSignIn';
import * as Sentry from '@sentry/react';
import config from '../config';
import {
  getStoredToken,
  getStoredRefreshToken,
  getValidToken,
  shouldRefreshToken,
  isTokenExpired,
  refreshToken,
  clearTokens,
  decodeToken,
  startBackgroundRefresh,
  stopBackgroundRefresh,
} from '../services/tokenRefreshService';
import Logger from '../utils/Logger';
import { identifySentryUser, clearSentryUser } from '../utils/sentry';
import { clearUserPreferencesCache } from '../hooks/useUserPreferences';

const authURL = `${config.API_URL}/auth`;

interface LoginParams {
  username: string;
  password: string;
}

const authProvider: AuthProvider = {
  /**
   * LOGIN - Authenticate user and store tokens
   */
  login: async ({ username, password }: LoginParams) => {
    const loginStart = Date.now();
    const request = new Request(`${authURL}/sing-in`, {
      method: 'POST',
      body: JSON.stringify({
        email: username,
        password: password,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    const response = await fetch(request);

    if (response.status < 200 || response.status >= 300) {
      Sentry.metrics.count('admin.auth.login', 1, { attributes: { result: 'failure', status: String(response.status) } });
      throw new Error(response.statusText);
    }

    const { idToken, refreshToken: refreshTokenValue, accessToken, deviceKey, roles } = await response.json();

    // Customers use the mobile app; Tablet accounts use the order tablet.
    const refusal = adminSignInRefusal(roles);
    if (refusal) throw new Error(refusal);

    localStorage.setItem('token', idToken);
    localStorage.setItem('refreshToken', refreshTokenValue);
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (deviceKey) {
      localStorage.setItem('deviceKey', deviceKey);
    }
    // Store roles for use by other components
    if (roles) {
      localStorage.setItem('userRoles', JSON.stringify(roles));
    }
    const loginDurationMs = Date.now() - loginStart;
    Sentry.metrics.count('admin.auth.login', 1, { attributes: { result: 'success', method: 'email' } });
    Sentry.metrics.distribution('admin.auth.login_time', loginDurationMs, { unit: 'millisecond' });
    Logger.log('[AuthProvider] Login successful', { roles });

    // Identify user in Sentry for error tracking
    const decoded = decodeToken(idToken);
    if (decoded?.sub) {
      identifySentryUser(decoded.sub, {
        email: decoded.email,
        name: decoded.name || decoded.email,
      });
    }

    // Start background token refresh (keeps session alive during idle periods)
    startBackgroundRefresh();

    // Default: let React-Admin handle redirect (goes to first resource)
    return undefined;
  },

  /**
   * LOGOUT - Clear all tokens
   */
  logout: async () => {
    Logger.log('[AuthProvider] Logging out');
    Sentry.metrics.count('admin.auth.logout', 1);
    clearUserPreferencesCache();
    stopBackgroundRefresh();
    clearSentryUser();
    clearTokens();
    return undefined;
  },

  /**
   * CHECK ERROR - Handle authentication errors
   * Note: Most 401 handling is now done by httpClient interceptors
   * This is a fallback for errors that bypass the interceptors
   */
  checkError: async (error: any) => {
    const status = error?.status;

    // 401 means token expired - try to refresh
    if (status === 401) {
      const token = getStoredToken();
      const storedRefreshToken = getStoredRefreshToken();

      if (!token || !storedRefreshToken) {
        // No tokens available, redirect to login
        Logger.warn('[AuthProvider] No tokens available for refresh');
        clearTokens();
        throw new Error('Session expired');
      }

      // Check if token is completely expired
      if (isTokenExpired(token)) {
        Logger.log('[AuthProvider] Token expired, attempting refresh...');
        try {
          const newToken = await refreshToken();
          if (newToken) {
            Logger.log('[AuthProvider] Token refreshed successfully via checkError');
            return;
          }
          // Refresh failed — DON'T clear tokens on first failure.
          // Could be a transient error (network blip, ALB 502, Cognito throttle).
          // Let the next request attempt trigger another refresh.
          Logger.warn('[AuthProvider] Token refresh returned null — keeping session (may retry)');
          return;
        } catch (refreshError) {
          // Only clear tokens if the refresh token itself is confirmed invalid
          // (not on network errors or transient failures)
          Logger.warn('[AuthProvider] Token refresh error in checkError — keeping session:', refreshError);
          return;
        }
      }

      // Token not expired but got 401 - something else is wrong
      Logger.warn('[AuthProvider] Got 401 but token not expired - possible permission issue');
      return;
    }

    // 403 means authenticated but not authorized - don't log out, just show error
    // This is a permissions issue, not an authentication issue
    if (status === 403) {
      Logger.log('[AuthProvider] 403 Forbidden - user not authorized for this action');
      return; // Keep user logged in, just show the error
    }

    return;
  },

  /**
   * CHECK AUTH — Called by React-Admin on every navigation/route change.
   *
   * WHY PREEMPTIVE REFRESH HERE:
   * If we only refreshed on 401 (response interceptor), the user would see a
   * brief loading state while the failed request retries. Refreshing here —
   * before any data requests fire — ensures the token is valid by the time
   * React-Admin renders the next page.
   *
   * WHY AWAIT (not fire-and-forget):
   * Previously this was `refreshToken().then(...)` without await. That meant
   * checkAuth() returned immediately (token still old), React-Admin fired data
   * requests with the old token, those hit 401, the response interceptor started
   * ANOTHER refresh, and the two refreshes raced. Now we await so the token is
   * guaranteed fresh before checkAuth() resolves.
   *
   * WHY NO window.location.reload():
   * Previously called reload() after fetching roles, which caused a visible
   * white flash. React-Admin calls getPermissions() after checkAuth(), which
   * reads roles from localStorage — no reload needed.
   *
   * ZOMBIE TOKEN (tab close during refresh):
   * If the user closes the tab while refreshToken() is in-flight:
   * - The JS context is destroyed, all promises are GC'd
   * - localStorage may contain old OR new tokens depending on timing
   * - On next visit, checkAuth() runs again: valid token → proceed,
   *   expired token → refresh, no token → redirect to login
   * - No corruption is possible; worst case is an extra login
   */
  checkAuth: async () => {
    const token = getStoredToken();

    if (!token) {
      Logger.log('[AuthProvider] No token found, redirecting to login');
      throw new Error('No token found');
    }

    // If we have a token but no roles, fetch them from the backend.
    // Uses getValidToken() to ensure the token is fresh before making the call.
    let storedRoles = localStorage.getItem('userRoles');
    if (!storedRoles) {
      Logger.log('[AuthProvider] No roles in localStorage, fetching from backend...');
      try {
        const validToken = await getValidToken();
        if (!validToken) {
          Logger.warn('[AuthProvider] Could not get valid token for roles fetch');
          clearTokens();
          throw new Error('Session expired');
        }
        const response = await fetch(`${config.API_URL}/users/profile`, {
          headers: { Authorization: `Bearer ${validToken}` },
        });
        if (response.ok) {
          const user = await response.json();
          if (user.roles) {
            localStorage.setItem('userRoles', JSON.stringify(user.roles));
            storedRoles = JSON.stringify(user.roles);
            Logger.log('[AuthProvider] Roles fetched and stored:', user.roles);
            // No reload — React-Admin calls getPermissions() which reads from localStorage.
            // Components will pick up the roles on next render cycle.
          }
        }
      } catch (e) {
        Logger.warn('[AuthProvider] Failed to fetch roles:', e);
      }
    }

    const roles = storedRoles ? JSON.parse(storedRoles) : [];

    // Check if token is completely expired
    if (isTokenExpired(token)) {
      Logger.log('[AuthProvider] Token expired, attempting refresh in checkAuth...');
      try {
        const newToken = await refreshToken();
        if (newToken) {
          Logger.log('[AuthProvider] Token refreshed successfully in checkAuth');
          return;
        }
        // Refresh failed — check if tokens were cleared (permanent Cognito rejection)
        const currentRefresh = getStoredRefreshToken();
        if (!currentRefresh) {
          // Tokens were cleared by tokenRefreshService (permanent 401/403)
          // OR refresh token was already missing — redirect to login
          Logger.error('[AuthProvider] No refresh token after failed refresh — redirecting to login');
          throw new Error('Session expired');
        }
        // Refresh token still exists — transient failure, keep session for retry
        Logger.warn('[AuthProvider] Token refresh failed (transient) — keeping session');
        return;
      } catch (error) {
        const currentRefresh = getStoredRefreshToken();
        if (!currentRefresh) {
          Logger.error('[AuthProvider] Refresh error + no refresh token — redirecting to login');
          throw error;
        }
        Logger.warn('[AuthProvider] Refresh error (transient) — keeping session');
        return;
      }
    }

    // Preemptive refresh: MUST await (not fire-and-forget).
    // Without await, checkAuth() returns → React-Admin fires data requests with
    // the OLD token → those requests hit 401 → response interceptor starts a
    // SECOND refresh → two parallel refreshes race against each other.
    // The singleton gate in tokenRefreshService prevents duplicate Cognito calls,
    // but awaiting here avoids the unnecessary 401 round-trip entirely.
    if (shouldRefreshToken(token)) {
      Logger.log('[AuthProvider] Token expiring soon, triggering preemptive refresh...');
      try {
        const newToken = await refreshToken();
        if (newToken) {
          Logger.log('[AuthProvider] Preemptive token refresh successful');
        }
      } catch (error) {
        // Don't block the user — interceptors will retry on next request
        Logger.warn('[AuthProvider] Preemptive refresh failed, will retry on next request:', error);
      }
    }

    // Token is valid — ensure background refresh is running (handles page reload)
    startBackgroundRefresh();
    return;
  },

  /**
   * GET PERMISSIONS - Return user roles/permissions
   */
  getPermissions: async () => {
    const storedRoles = localStorage.getItem('userRoles');
    const roles = storedRoles ? JSON.parse(storedRoles) : [];
    return roles;
  },

  /**
   * GET IDENTITY - Return user identity for display
   */
  getIdentity: async () => {
    const token = getStoredToken();
    if (!token) {
      throw new Error('No token');
    }

    try {
      const decoded = decodeToken(token);
      return {
        id: decoded?.sub || 'unknown',
        // The Deeliver DB user id (cognito:username). `id` stays the Cognito sub;
        // this is what the backend and socket events call the user's id.
        userId: decoded?.['cognito:username'] || decoded?.sub,
        fullName: decoded?.email || 'User',
        avatar: undefined,
      };
    } catch {
      return {
        id: 'unknown',
        fullName: 'User',
        avatar: undefined,
      };
    }
  },
};

export default authProvider;
